package com.evoapps.evoshift.navigation

import org.jetbrains.compose.resources.stringResource
import evoshift.composeapp.generated.resources.shortcut_go_to
import evoshift.composeapp.generated.resources.shortcut_group_dialogs
import evoshift.composeapp.generated.resources.shortcut_close_dialog
import evoshift.composeapp.generated.resources.shortcut_confirm_dialog
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEvent
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isAltPressed
import androidx.compose.ui.input.key.isCtrlPressed
import androidx.compose.ui.input.key.isMetaPressed
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.type
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.roster_next_month
import evoshift.composeapp.generated.resources.roster_previous_month
import evoshift.composeapp.generated.resources.shortcut_group_actions
import evoshift.composeapp.generated.resources.shortcut_group_month
import evoshift.composeapp.generated.resources.shortcut_group_navigation
import evoshift.composeapp.generated.resources.shortcut_new_roster
import evoshift.composeapp.generated.resources.shortcut_print
import evoshift.composeapp.generated.resources.shortcut_settings
import evoshift.composeapp.generated.resources.shortcut_copy_cell
import evoshift.composeapp.generated.resources.shortcut_grid_move
import evoshift.composeapp.generated.resources.shortcut_grid_open
import evoshift.composeapp.generated.resources.shortcut_grid_select
import evoshift.composeapp.generated.resources.shortcut_grid_unassign
import evoshift.composeapp.generated.resources.shortcut_group_roster
import evoshift.composeapp.generated.resources.shortcut_paste_cell
import evoshift.composeapp.generated.resources.shortcut_undo
import evoshift.composeapp.generated.resources.shortcut_zoom_in
import evoshift.composeapp.generated.resources.shortcut_zoom_out
import evoshift.composeapp.generated.resources.shortcut_zoom_reset
import org.jetbrains.compose.resources.StringResource

/*
 * SH-98: the app's keyboard shortcuts, the same as the macOS app's menu (SH-97): Ctrl on Android
 * laptops (Googlebook, Chromebooks, tablets with a keyboard), Cmd (Meta) on a Mac keyboard.
 *
 * - The shell (App.kt) handles navigation: the tabs, settings, a new roster.
 * - Screens handle their own actions while they show ([ShortcutHandler]): Print on the roster
 *   builder and the payroll, the month on every screen whose top bar shows it.
 * - Keys reach the app twice over: Compose's key events while something in the app has focus
 *   (App.kt), and Android's Activity.onKeyShortcut when nothing has (MainActivity); only one of
 *   them sees a given key press.
 * - Android's Keyboard Shortcuts Helper (Meta + /) lists [KeyboardShortcuts.help]: what works on
 *   the screen shown, in the app's language.
 */

/** A key with Ctrl (or Cmd); no Shift or Alt. */
data class KeyChord(val key: Key)

/** What the section of the helper a shortcut belongs to says. */
enum class ShortcutGroup(val title: StringResource) {
    NAVIGATION(Res.string.shortcut_group_navigation),
    MONTH(Res.string.shortcut_group_month),
    ACTIONS(Res.string.shortcut_group_actions),
    /** SH-169: the roster grid's keys (while the grid shows). */
    ROSTER(Res.string.shortcut_group_roster)
}

/**
 * A shortcut: its keys (the first is the one the helper shows first), its group and, for all but
 * the tabs (labelled by the tab, see App.kt), its label.
 */
enum class AppShortcut(val group: ShortcutGroup, val chords: List<KeyChord>, val label: StringResource?) {
    NEW_ROSTER(ShortcutGroup.ACTIONS, listOf(KeyChord(Key.N)), Res.string.shortcut_new_roster),
    PRINT(ShortcutGroup.ACTIONS, listOf(KeyChord(Key.P)), Res.string.shortcut_print),
    PREVIOUS_MONTH(
        ShortcutGroup.MONTH,
        listOf(KeyChord(Key.LeftBracket), KeyChord(Key.DirectionLeft), KeyChord(Key.PageUp)),
        Res.string.roster_previous_month
    ),
    NEXT_MONTH(
        ShortcutGroup.MONTH,
        listOf(KeyChord(Key.RightBracket), KeyChord(Key.DirectionRight), KeyChord(Key.PageDown)),
        Res.string.roster_next_month
    ),
    TAB_1(ShortcutGroup.NAVIGATION, listOf(KeyChord(Key.One), KeyChord(Key.NumPad1)), null),
    TAB_2(ShortcutGroup.NAVIGATION, listOf(KeyChord(Key.Two), KeyChord(Key.NumPad2)), null),
    TAB_3(ShortcutGroup.NAVIGATION, listOf(KeyChord(Key.Three), KeyChord(Key.NumPad3)), null),
    TAB_4(ShortcutGroup.NAVIGATION, listOf(KeyChord(Key.Four), KeyChord(Key.NumPad4)), null),
    TAB_5(ShortcutGroup.NAVIGATION, listOf(KeyChord(Key.Five), KeyChord(Key.NumPad5)), null),
    SETTINGS(ShortcutGroup.NAVIGATION, listOf(KeyChord(Key.Comma)), Res.string.shortcut_settings),
    // SH-169: the roster grid. Text fields keep their own Ctrl+Z / C / V (they use the key first).
    UNDO(ShortcutGroup.ROSTER, listOf(KeyChord(Key.Z)), Res.string.shortcut_undo),
    COPY_CELL(ShortcutGroup.ROSTER, listOf(KeyChord(Key.C)), Res.string.shortcut_copy_cell),
    PASTE_CELL(ShortcutGroup.ROSTER, listOf(KeyChord(Key.V)), Res.string.shortcut_paste_cell),
    ZOOM_IN(ShortcutGroup.ROSTER, listOf(KeyChord(Key.Equals), KeyChord(Key.Plus), KeyChord(Key.NumPadAdd)), Res.string.shortcut_zoom_in),
    ZOOM_OUT(ShortcutGroup.ROSTER, listOf(KeyChord(Key.Minus), KeyChord(Key.NumPadSubtract)), Res.string.shortcut_zoom_out),
    ZOOM_RESET(ShortcutGroup.ROSTER, listOf(KeyChord(Key.Zero), KeyChord(Key.NumPad0)), Res.string.shortcut_zoom_reset);

    /** For TAB_n: the tab's index in the bar (0-based); null for the others. */
    val tabIndex: Int? get() = listOf(TAB_1, TAB_2, TAB_3, TAB_4, TAB_5).indexOf(this).takeIf { it >= 0 }

    companion object {
        /** The shortcut of the tab at [index] in the bar, if there is one (at most five tabs). */
        fun forTab(index: Int): AppShortcut? = listOf(TAB_1, TAB_2, TAB_3, TAB_4, TAB_5).getOrNull(index)

        /**
         * The shortcut [key] makes with these modifiers: Ctrl or Cmd, without Shift or Alt (Ctrl +
         * Shift + … stays the text fields' and the system's).
         */
        fun of(key: Key, ctrl: Boolean, meta: Boolean, shift: Boolean, alt: Boolean): AppShortcut? {
            if (!(ctrl || meta) || shift || alt) return null
            return entries.firstOrNull { shortcut -> shortcut.chords.any { it.key == key } }
        }
    }
}

/** The shortcut of a key press (key down only), or null. */
fun KeyEvent.appShortcut(): AppShortcut? =
    if (type != KeyEventType.KeyDown) null
    else AppShortcut.of(key, ctrl = isCtrlPressed, meta = isMetaPressed, shift = isShiftPressed, alt = isAltPressed)

/** One line of the shortcut helper: [label] (in the app's language) for [key], with Ctrl when [ctrl]. */
data class ShortcutHelpKey(val label: String, val key: Key, val ctrl: Boolean = true)

/** A section of the shortcut helper. */
data class ShortcutHelpGroup(val title: String, val keys: List<ShortcutHelpKey>)

/**
 * The keys the helper shows for a shortcut: the bracket and the arrow for the months (not Page
 * Up / Down), the number row for the tabs (not the keypad).
 */
val AppShortcut.helpChords: List<KeyChord>
    get() = when (group) {
        ShortcutGroup.MONTH -> chords.take(2)
        else -> chords.take(1)
    }

/**
 * Who handles shortcuts: the screens on top ([register], the newest first) and then the shell.
 * One per app window (App.kt), provided to screens as [LocalAppShortcuts].
 */
@Stable
class AppShortcuts {
    private class Registration(val shortcut: AppShortcut, val action: () -> Unit)

    // Snapshot state: the shell lists what the screens offer in the helper.
    private val registrations = mutableStateListOf<Registration>()

    /** The shell's own shortcuts (App.kt): true when it handled it. */
    var shell: (AppShortcut) -> Boolean = { false }

    /** The shortcuts the screens on display handle (e.g. Print on the roster builder). */
    val screenShortcuts: Set<AppShortcut> get() = registrations.mapTo(mutableSetOf()) { it.shortcut }

    internal fun register(shortcut: AppShortcut, action: () -> Unit): Any =
        Registration(shortcut, action).also { registrations.add(it) }

    internal fun unregister(token: Any) {
        registrations.removeAll { it === token }
    }

    /** Does [shortcut]: the newest screen that handles it, else the shell. True when done. */
    fun handle(shortcut: AppShortcut): Boolean {
        registrations.lastOrNull { it.shortcut == shortcut }?.let {
            it.action()
            return true
        }
        return shell(shortcut)
    }

    /** Handles a Compose key event (App.kt's key listener). */
    fun handle(event: KeyEvent): Boolean = event.appShortcut()?.let(::handle) ?: false
}

val LocalAppShortcuts = staticCompositionLocalOf<AppShortcuts?> { null }

/**
 * The app's shortcuts as the platform sees them (Android's Activity: onKeyShortcut and the
 * shortcut helper): the window's [AppShortcuts] and what the helper lists, kept by App.kt.
 */
object KeyboardShortcuts {
    /** The shown window's shortcuts; null before the app shell is composed or after it left. */
    var active: AppShortcuts? = null

    /** What works now, grouped and labelled for the helper (App.kt keeps it current). */
    var help: List<ShortcutHelpGroup> = emptyList()

    /** Does [shortcut] in the shown window: true when something handled it. */
    fun handle(shortcut: AppShortcut): Boolean = active?.handle(shortcut) ?: false
}

/**
 * While this composable shows, [shortcut] does [action] (when [enabled]): e.g. Ctrl+P prints the
 * month on the roster builder. The newest screen that registered a shortcut handles it.
 */
@Composable
fun ShortcutHandler(shortcut: AppShortcut, enabled: Boolean = true, action: () -> Unit) {
    val shortcuts = LocalAppShortcuts.current ?: return
    if (!enabled) return
    val current by rememberUpdatedState(action)
    DisposableEffect(shortcuts, shortcut) {
        val token = shortcuts.register(shortcut) { current() }
        onDispose { shortcuts.unregister(token) }
    }
}

/**
 * What the shortcut helper lists ([on]: in the signed-in app), in the app's language: the
 * [available] shortcuts by group (a tab's by [tabs]' names: "Go to Rosters"), then the dialogs'
 * Esc and Enter.
 */
@Composable
fun shortcutHelp(on: Boolean, tabs: List<AppTab>, available: Set<AppShortcut>): List<ShortcutHelpGroup> {
    val tabLabels = tabs.map { stringResource(Res.string.shortcut_go_to, stringResource(it.label())) }
    val labels = AppShortcut.entries.associateWith { shortcut -> shortcut.label?.let { stringResource(it) } }
    val titles = ShortcutGroup.entries.associateWith { stringResource(it.title) }
    val dialogs = stringResource(Res.string.shortcut_group_dialogs)
    val close = stringResource(Res.string.shortcut_close_dialog)
    val confirm = stringResource(Res.string.shortcut_confirm_dialog)
    // SH-169: the grid's own keys (no Ctrl), while it shows (its zoom keys are then on offer).
    val gridKeys = listOf(
        ShortcutHelpKey(stringResource(Res.string.shortcut_grid_move), Key.DirectionRight, ctrl = false),
        ShortcutHelpKey(stringResource(Res.string.shortcut_grid_open), Key.Enter, ctrl = false),
        ShortcutHelpKey(stringResource(Res.string.shortcut_grid_select), Key.Spacebar, ctrl = false),
        ShortcutHelpKey(stringResource(Res.string.shortcut_grid_unassign), Key.Delete, ctrl = false)
    )
    if (!on) return emptyList()
    return buildList {
        ShortcutGroup.entries.forEach { group ->
            val keys = AppShortcut.entries.filter { it.group == group && it in available }.flatMap { shortcut ->
                val label = shortcut.tabIndex?.let { tabLabels.getOrNull(it) } ?: labels[shortcut]
                if (label == null) emptyList() else shortcut.helpChords.map { ShortcutHelpKey(label, it.key) }
            } + (if (group == ShortcutGroup.ROSTER && AppShortcut.ZOOM_IN in available) gridKeys else emptyList())
            if (keys.isNotEmpty()) add(ShortcutHelpGroup(titles.getValue(group), keys))
        }
        add(ShortcutHelpGroup(dialogs, listOf(ShortcutHelpKey(close, Key.Escape, ctrl = false), ShortcutHelpKey(confirm, Key.Enter, ctrl = false))))
    }
}
